// Beat 9 visual: a scrapbook-style polaroid triptych of off-stream group
// settings (friends at home, the team in the office, and a wider friend
// group). Each polaroid scrolls in on its own timing — left first, right
// second, team-in-the-middle landing last on top of the pile — then peels
// off in reverse as the beat exits.
//
// Layout switches between a vertical stack on mobile portrait viewports
// (where a horizontal triptych shrinks the photos past legibility) and a
// horizontal triptych on sm+ viewports. The same per-polaroid entry/exit
// timing applies to both layouts.

import React from 'react';
import { motion, useTransform, type MotionValue } from 'framer-motion';

import { ASSETS } from '@airhead-games/shared/configs';
import {
  easeOutCubic,
  mapProgress,
} from '../../../utilities/scrollTimelineMath';

type BeatNineCommunitiesProps = {
  scrollVh: MotionValue<number>;
  /** Global vh where the host segment starts. */
  segmentStartVh: number;
  /** Host segment duration in vh. */
  segmentDurationVh: number;
};

type Polaroid = {
  src: string;
  alt: string;
  /** Resting rotation in degrees. */
  rotationDeg: number;
  /** Stacking order — higher renders on top. Team lands on top. */
  z: number;
  /** Entrance window as % of host segment vh (0-100). */
  enterStartPct: number;
  enterEndPct: number;
  /** Exit window as % of host segment vh (0-100). */
  exitStartPct: number;
  exitEndPct: number;
  /** Horizontal offset (% of polaroid width) used in the mobile vertical
   *  stack to scatter the pile left/right of center. */
  verticalLayoutXOffsetPct: number;
  /** Vertical offset (% of polaroid height) used in the horizontal
   *  triptych so the three cards don't align in a perfect row. */
  horizontalLayoutYOffsetPct: number;
};

// DOM order is left → middle → right (controls the horizontal layout's
// flex order and the vertical stack's top-to-bottom order). Entry timing
// is independent of DOM order: left enters first, right second, team
// (middle) last so it lands on top of the pile. Entry windows are 22%
// of the segment each (wider than before so each card has time to read)
// and staggered so each next card starts ~mid-way through the previous
// one's entry — clear sequence, no jamming.
const POLAROIDS: Polaroid[] = [
  {
    src: ASSETS.heroHome,
    alt: 'Friends playing Airhead Games at home',
    rotationDeg: -6,
    z: 1,
    enterStartPct: 0,
    enterEndPct: 22,
    exitStartPct: 85,
    exitEndPct: 95,
    verticalLayoutXOffsetPct: -14,
    horizontalLayoutYOffsetPct: 6,
  },
  {
    src: ASSETS.heroTeam,
    alt: 'Co-workers playing Airhead Games in the office',
    rotationDeg: 2,
    z: 3,
    enterStartPct: 36,
    enterEndPct: 58,
    exitStartPct: 73,
    exitEndPct: 85,
    verticalLayoutXOffsetPct: 10,
    horizontalLayoutYOffsetPct: -4,
  },
  {
    src: ASSETS.heroFriends,
    alt: 'A group of friends playing together',
    rotationDeg: -3,
    z: 2,
    enterStartPct: 18,
    enterEndPct: 40,
    exitStartPct: 79,
    exitEndPct: 90,
    verticalLayoutXOffsetPct: -8,
    horizontalLayoutYOffsetPct: 8,
  },
];

const POLAROID_ENTRY_Y_PX = 60;
const POLAROID_EXIT_Y_PX = -40;

const polaroidFrameClassName = [
  'relative bg-white',
  // Polaroid borders: tighter on mobile, growing with breakpoint.
  'p-2 pb-8 sm:p-3 sm:pb-10 md:p-4 md:pb-12 lg:p-5 lg:pb-14',
  'shadow-[0_18px_40px_-12px_rgba(0,0,0,0.45)]',
].join(' ');

const polaroidImageClassName = 'block aspect-[4/3] w-full object-cover';

type LayoutMode = 'verticalStack' | 'horizontalTriptych';

const PolaroidCard: React.FC<{
  polaroid: Polaroid;
  scrollVh: MotionValue<number>;
  segmentStartVh: number;
  segmentDurationVh: number;
  /** Tailwind class string for sizing + spacing (layout-specific). */
  layoutClassName: string;
  /** Which layout we're in — controls which static offset applies. */
  layoutMode: LayoutMode;
}> = ({
  polaroid,
  scrollVh,
  segmentStartVh,
  segmentDurationVh,
  layoutClassName,
  layoutMode,
}) => {
  const enterStartVh =
    segmentStartVh + (polaroid.enterStartPct / 100) * segmentDurationVh;
  const enterEndVh =
    segmentStartVh + (polaroid.enterEndPct / 100) * segmentDurationVh;
  const exitStartVh =
    segmentStartVh + (polaroid.exitStartPct / 100) * segmentDurationVh;
  const exitEndVh =
    segmentStartVh + (polaroid.exitEndPct / 100) * segmentDurationVh;

  const opacity = useTransform(scrollVh, (v) => {
    const entered = easeOutCubic(mapProgress(v, enterStartVh, enterEndVh));
    const exited = easeOutCubic(mapProgress(v, exitStartVh, exitEndVh));
    return Math.max(0, entered - exited);
  });

  const y = useTransform(scrollVh, (v) => {
    const entered = easeOutCubic(mapProgress(v, enterStartVh, enterEndVh));
    const exited = easeOutCubic(mapProgress(v, exitStartVh, exitEndVh));
    const entryOffset = (1 - entered) * POLAROID_ENTRY_Y_PX;
    const exitOffset = exited * POLAROID_EXIT_Y_PX;
    return entryOffset + exitOffset;
  });

  // Static layout offsets composed via CSS `translate` (separate from
  // framer-motion's animated `transform`, so they stack without
  // fighting each other). Vertical stack uses an x-offset to scatter
  // the pile; horizontal triptych uses a y-offset so the three cards
  // don't sit in a perfect row.
  const restingXPct =
    layoutMode === 'verticalStack' ? polaroid.verticalLayoutXOffsetPct : 0;
  const restingYPct =
    layoutMode === 'horizontalTriptych'
      ? polaroid.horizontalLayoutYOffsetPct
      : 0;

  return (
    <motion.div
      className={`${polaroidFrameClassName} ${layoutClassName}`}
      // Force each polaroid onto its own permanently-promoted compositor
      // layer (translateZ(0) + will-change). Same mobile-WebKit hardening as
      // the Beat 6 pyramid bricks: when the host ScrollContent wrapper's
      // opacity envelope rises 0→1 just before the cards animate (scrolling
      // UP into the beat), descendants flattened into the parent layer can
      // have their opacity/transform repaints dropped, stranding a card
      // mid-fade. A self-owned backing store keeps each card painting
      // reliably regardless of the parent's opacity transition.
      transformTemplate={(_, generated) =>
        generated === 'none' ? 'translateZ(0)' : `${generated} translateZ(0)`
      }
      style={{
        opacity,
        y,
        rotate: polaroid.rotationDeg,
        zIndex: polaroid.z,
        translate: `${restingXPct}% ${restingYPct}%`,
        willChange: 'transform, opacity',
      }}
    >
      <img
        src={polaroid.src}
        alt={polaroid.alt}
        className={polaroidImageClassName}
        loading="lazy"
      />
    </motion.div>
  );
};

const BeatNineCommunities: React.FC<BeatNineCommunitiesProps> = ({
  scrollVh,
  segmentStartVh,
  segmentDurationVh,
}) => {
  return (
    <>
      {/* Mobile portrait (< sm): vertical stack. Card width is bounded by
          both viewport width (so it can't push past the screen edges) and
          available viewport height (so 3 stacked cards + overlaps always
          fit, even on short phones like iPhone SE 375x667). Overlap is
          expressed in svh so it scales with the available column height,
          not the viewport width. */}
      <div className="flex w-full flex-col items-center sm:hidden">
        {POLAROIDS.map((polaroid, index) => (
          <PolaroidCard
            key={polaroid.src}
            polaroid={polaroid}
            scrollVh={scrollVh}
            segmentStartVh={segmentStartVh}
            segmentDurationVh={segmentDurationVh}
            layoutMode="verticalStack"
            layoutClassName={[
              'w-[min(68vw,26svh)]',
              // ~22% overlap: leaves ~78% of each polaroid visible so
              // the stack reads as a casual pile, not jammed together.
              index === 0 ? '' : '-mt-[5svh]',
            ].join(' ')}
          />
        ))}
      </div>

      {/* sm+: horizontal triptych. Card width is bounded by viewport width
          (so the triptych always fits horizontally with the overlap math
          below) AND by svh (so on short laptops — 1366x768, 1280x720 —
          card height never exceeds the available shell area). Overlap is
          in vw so the trio's total width is predictable regardless of how
          tall the viewport is. A max-height override clamps the svh side
          tighter on landscape-phone viewports (max-h:500) where the
          regular svh fraction would still overflow. */}
      <div className="relative hidden w-full max-w-[1180px] items-center justify-center sm:flex">
        {POLAROIDS.map((polaroid, index) => (
          <PolaroidCard
            key={polaroid.src}
            polaroid={polaroid}
            scrollVh={scrollVh}
            segmentStartVh={segmentStartVh}
            segmentDurationVh={segmentDurationVh}
            layoutMode="horizontalTriptych"
            layoutClassName={[
              'shrink-0',
              'sm:w-[min(34vw,52svh)]',
              'md:w-[min(32vw,55svh)]',
              'lg:w-[min(28vw,58svh)]',
              'xl:w-[min(26vw,60svh)]',
              '2xl:w-[min(24vw,62svh)]',
              'min-[1920px]:w-[min(22vw,62svh)]',
              'min-[2560px]:w-[min(20vw,65svh)]',
              // Landscape-phone clamp: ~375-500px tall, ≥640px wide.
              // Without this, 52svh of 375 = 195 → card height ~180,
              // which overflows the ~150px available shell area.
              '[@media(max-height:500px)]:w-[min(30vw,38svh)]',
              index === 0
                ? ''
                : 'sm:-ml-[5vw] md:-ml-[4vw] lg:-ml-[3vw] xl:-ml-[2.5vw] 2xl:-ml-[2vw]',
            ].join(' ')}
          />
        ))}
      </div>
    </>
  );
};

export default BeatNineCommunities;
